import React, { useEffect, useRef } from "react";
import styled, { css } from "styled-components";

import { formatTHB } from "../../views/SubscriptionProjectV2/lib/formatters";
import { useProjectPlans } from "../../hook/Subscription/useProjectPlans";

/**
 * Plan carousel — horizontally scrollable cards using native CSS scroll-snap.
 *
 * Containment via the `minmax(0, 1fr)` grid trick:
 *
 *  Standard CSS approaches (overflow:hidden + width:100% + min-width:0)
 *  *should* contain CardsRow's `width: max-content`, but in practice
 *  browsers' intrinsic-sizing algorithms still let it propagate up the
 *  layout tree in some cases — making ancestors (Form, StepShell, grid
 *  tracks) expand and overflow the page horizontally.
 *
 *  The fix: make CarouselWrapper a CSS Grid with a single track sized
 *  `minmax(0, 1fr)`. This tells the grid algorithm the track is 0..1fr
 *  — a hard ceiling at 1fr (= parent width) and a hard floor at 0 (=
 *  ignore content's intrinsic min-size). Once a child sits in that
 *  track, it CANNOT force the track wider. Period.
 */
function PlanCarousel({ id_project, selectedId, onSelect, lang = "th" }) {
  const { data: plans = [], isLoading, error } = useProjectPlans({ id_project });
  const rowRef = useRef(null);

  // Auto-scroll to selected card when selection changes (e.g. URL ?plan=42)
  useEffect(() => {
    if (!rowRef.current || !selectedId) return;
    const idx = plans.findIndex(
      (p) => Number(p.id_subscription_plan_table) === Number(selectedId)
    );
    if (idx < 0) return;

    const card = rowRef.current.children[idx];
    card?.scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "nearest",
    });
  }, [selectedId, plans]);

  if (isLoading) {
    return (
      <Status>
        {lang === "en" ? "Loading plans…" : "กำลังโหลดแพ็กเกจ…"}
      </Status>
    );
  }

  if (error || plans.length === 0) {
    return (
      <Status $error>
        {lang === "en" ? "No plans available" : "ไม่มีแพ็กเกจให้เลือก"}
      </Status>
    );
  }

  return (
    <CarouselWrapper>
      <ScrollContainer>
        <CardsRow ref={rowRef}>
          {plans.map((plan) => {
            const isSelected =
              Number(plan.id_subscription_plan_table) === Number(selectedId);

            return (
              <Card
                key={plan.id_subscription_plan_table}
                type="button"
                onClick={() => onSelect?.(plan)}
                $selected={isSelected}
              >
                <PlanName>
                  {plan.name_subscription_plan}
                  {isSelected && <Tick aria-hidden> ✓</Tick>}
                </PlanName>

                <Price>{formatTHB(plan.price)}</Price>

                <Period>
                  {plan.installment_plan === "YEARLY"
                    ? lang === "en"
                      ? "/ year"
                      : "/ ปี"
                    : lang === "en"
                    ? "/ month"
                    : "/ เดือน"}
                </Period>

                <SelectAction $selected={isSelected}>
                  {isSelected
                    ? lang === "en"
                      ? "Selected"
                      : "เลือกแล้ว"
                    : lang === "en"
                    ? "Select"
                    : "เลือก"}
                </SelectAction>
              </Card>
            );
          })}
        </CardsRow>
      </ScrollContainer>
    </CarouselWrapper>
  );
}

export default PlanCarousel;

// ─── Styled ──────────────────────────────────

const Status = styled.div`
  text-align: center;
  padding: 32px 16px;
  font-size: 13px;
  color: ${({ $error }) => ($error ? "#ef4444" : "#9ca3af")};
`;

/**
 * CarouselWrapper — grid with minmax(0, 1fr) is the definitive containment.
 *
 *   display: grid + grid-template-columns: minmax(0, 1fr)
 *      → Grid algorithm enforces a single column from 0 to 1fr.
 *      → Children CANNOT push the column wider than 1fr (= parent width).
 *      → ScrollContainer (the only child) is forced to fit parent width
 *        regardless of CardsRow's wide intrinsic size.
 *
 *   overflow: hidden is now belt-and-suspenders (visual clip).
 */
const CarouselWrapper = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  width: 100%;
  max-width: 100%;
  overflow: hidden;
`;

/**
 * ScrollContainer — the actual scroll viewport.
 *  - overflow-x:auto enables horizontal scroll for the wide CardsRow inside
 *  - overflow-y:hidden absorbs the small vertical movement from selected
 *    card's translateY(-2px), preventing accidental Y scroll
 *  - scroll-snap-type: x mandatory makes scroll always settle on a card
 *  - Now safely sized to grid track (0..1fr), so its overflow-x:auto
 *    clipping works against a guaranteed parent-width boundary.
 */
const ScrollContainer = styled.div`
  overflow-x: auto;
  overflow-y: hidden;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  padding: 8px 0 16px;

  /* Subtle scrollbar — Firefox */
  scrollbar-width: thin;
  scrollbar-color: #c7d2fe transparent;

  /* Subtle scrollbar — WebKit */
  &::-webkit-scrollbar {
    height: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #c7d2fe;
    border-radius: 3px;

    &:hover {
      background: #6366f1;
    }
  }
`;

/**
 * CardsRow — inner track, wider than ScrollContainer, triggers scroll.
 */
const CardsRow = styled.div`
  display: flex;
  gap: 16px;
  padding: 4px 8px 8px;
  width: max-content;
`;

const cardSelectedStyle = css`
  border-color: #6366f1;
  background: rgba(99, 102, 241, 0.04);
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.15);
  transform: translateY(-2px);
`;

const Card = styled.button`
  && {
    flex: 0 0 220px;
    scroll-snap-align: start;
    background: #ffffff;
    border: 2px solid #e5e7eb;
    border-radius: 12px;
    padding: 20px 16px;
    cursor: pointer;
    text-align: center;
    transition: all 0.15s;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    font-family: inherit;

    &:hover {
      border-color: #c7d2fe;
    }

    ${({ $selected }) => $selected && cardSelectedStyle}
  }

  @media (max-width: 1024px) {
    && {
      flex-basis: 200px;
    }
  }

  @media (max-width: 480px) {
    && {
      flex-basis: 75vw;
      max-width: 240px;
    }
  }
`;

const PlanName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 4px;
`;

const Tick = styled.span`
  color: #6366f1;
  font-weight: 700;
`;

const Price = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: #111827;
  line-height: 1.1;
`;

const Period = styled.div`
  font-size: 11px;
  color: #6b7280;
  margin-bottom: 16px;
`;

const SelectAction = styled.div`
  width: 100%;
  margin-top: auto;
  padding: 8px 0;
  font-size: 13px;
  font-weight: 600;
  border-radius: 8px;
  transition: all 0.15s;

  ${({ $selected }) =>
    $selected
      ? css`
          background: #6366f1;
          color: #ffffff;
        `
      : css`
          background: transparent;
          color: #6366f1;
          border: 1px solid #e5e7eb;
        `}
`;